import React, { useState, useEffect } from "react";
import { DS } from "../theme";
import { getLeaderboard, getUserElo } from "../lib/api";

const MEDALS = { 1: "♔", 2: "♕", 3: "♖" };

export default function Leaderboard({ stats, user, elo }) {
  const [tab, setTab]         = useState("global");
  const [board, setBoard]     = useState(null);  // { players, userElo, userRank }
  const [eloData, setEloData] = useState(null);  // { elo, rank, history }
  const [loading, setLoading] = useState(false);

  const { wins = 0, losses = 0, draws = 0, gamesPlayed = 0, maxStreak = 0 } = stats;
  const winRate = gamesPlayed > 0 ? Math.round((wins / gamesPlayed) * 100) : 0;

  useEffect(() => {
    if (!user?.token) return;
    if (tab === "global" && !board) {
      setLoading(true);
      getLeaderboard(user.token)
        .then(setBoard)
        .catch(() => {})
        .finally(() => setLoading(false));
    }
    if (tab === "personal" && !eloData) {
      setLoading(true);
      getUserElo(user.token)
        .then(setEloData)
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [tab]); // eslint-disable-line

  const displayElo  = eloData?.elo  || elo  || 1200;
  const displayRank = eloData?.rank || board?.userRank || null;

  return (
    <div>
      {/* Tab toggle */}
      <div style={{ display: "flex", background: DS.elevated, borderRadius: 10, padding: 4, marginBottom: 16, gap: 4 }}>
        {[["global", "♕  Global"], ["personal", "◆  My Record"]].map(([t, lbl]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex: 1, padding: "8px 0", border: "none", borderRadius: 8, cursor: "pointer",
            fontFamily: "inherit", fontWeight: 600, fontSize: 13, transition: "all 0.15s",
            background: tab === t ? DS.accent : "transparent",
            color: tab === t ? "#fff" : DS.textMuted,
            boxShadow: tab === t ? "0 2px 8px rgba(115,102,240,0.3)" : "none",
          }}>{lbl}</button>
        ))}
      </div>

      {/* ── GLOBAL ── */}
      {tab === "global" && (
        <>
          {/* User's own rank card */}
          {user && (
            <div style={{
              ...card,
              background: `linear-gradient(135deg, ${DS.accent}14, ${DS.accent}06)`,
              border: `1px solid ${DS.accent}35`,
              marginBottom: 12,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: `${DS.accent}22`, border: `1px solid ${DS.accent}40`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16, color: DS.accent,
                  }}>♟</div>
                  <div>
                    <div className="cinzel" style={{ fontSize: 13, fontWeight: 700, color: DS.text }}>
                      {user.username}
                    </div>
                    <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 2 }}>
                      {displayRank ? `Rank #${displayRank}` : "Unranked"}
                    </div>
                  </div>
                </div>
                <div className="cinzel" style={{ fontSize: 22, fontWeight: 700, color: DS.accent }}>
                  {displayElo}
                  <div style={{ fontSize: 9, color: DS.textFaint, fontWeight: 400, textAlign: "right", marginTop: 2 }}>ELO</div>
                </div>
              </div>
            </div>
          )}

          {loading && !board && (
            <div style={{ textAlign: "center", padding: "32px 0", color: DS.textFaint, fontSize: 13 }}>
              Loading rankings…
            </div>
          )}

          {board && (
            <div style={card}>
              <div style={label}>Top Players</div>
              {board.players.length === 0 ? (
                <div style={{ fontSize: 13, color: DS.textFaint, textAlign: "center", padding: "20px 0" }}>
                  No ranked players yet — play an online game to appear here.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${DS.border}` }}>
                      {["#", "Player", "Elo"].map(h => (
                        <th key={h} style={{
                          padding: "6px 10px", textAlign: "left",
                          fontSize: 10, color: DS.textFaint, fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.06em",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {board.players.map(row => {
                      const isMe = row.username === user?.username;
                      return (
                        <tr key={row.rank} style={{
                          borderBottom: `1px solid ${DS.borderFaint}`,
                          background: isMe
                            ? `${DS.accent}10`
                            : row.rank <= 3 ? `${DS.gold}08` : "transparent",
                        }}>
                          <td style={{ padding: "10px 10px", fontWeight: 700, color: MEDALS[row.rank] ? DS.gold : DS.textFaint, width: 40 }}>
                            {MEDALS[row.rank] || row.rank}
                          </td>
                          <td style={{ padding: "10px 10px", fontWeight: isMe ? 700 : 500, color: isMe ? DS.accent : DS.text }}>
                            {row.username}{isMe ? " (you)" : ""}
                          </td>
                          <td style={{ padding: "10px 10px", fontWeight: 700, color: row.rank === 1 ? DS.gold : DS.text }}>
                            {row.elo}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {!user && (
            <div style={{ ...card, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: DS.textMuted }}>Sign in to see your ranking.</div>
            </div>
          )}
        </>
      )}

      {/* ── PERSONAL ── */}
      {tab === "personal" && (
        <>
          {/* Elo stat */}
          {user && (
            <div style={card}>
              <div style={label}>Elo Rating</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 16, marginBottom: eloData?.history?.length ? 16 : 0 }}>
                <div>
                  <div className="cinzel" style={{ fontSize: 32, fontWeight: 700, color: DS.accent, lineHeight: 1 }}>
                    {displayElo}
                  </div>
                  <div style={{ fontSize: 11, color: DS.textFaint, marginTop: 4 }}>
                    {displayRank ? `Global rank #${displayRank}` : "Play an online game to get ranked"}
                  </div>
                </div>
              </div>

              {eloData?.history?.length > 0 && (
                <>
                  <div style={{ fontSize: 11, color: DS.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, marginTop: 4 }}>
                    Recent games
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {eloData.history.slice(0, 8).map((h, i) => (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        fontSize: 12, padding: "6px 0",
                        borderBottom: i < 7 ? `1px solid ${DS.borderFaint}` : "none",
                      }}>
                        <span style={{
                          color: h.result === 'win' ? DS.success : h.result === 'draw' ? DS.warning : DS.danger,
                          fontWeight: 600, minWidth: 36,
                        }}>
                          {h.result === 'win' ? 'Win' : h.result === 'draw' ? 'Draw' : 'Loss'}
                        </span>
                        <span style={{ color: DS.textFaint, fontSize: 11 }}>
                          {h.old_elo} → {h.new_elo}
                        </span>
                        <span className="cinzel" style={{
                          fontWeight: 700, fontSize: 13,
                          color: h.change >= 0 ? DS.success : DS.danger,
                          minWidth: 42, textAlign: "right",
                        }}>
                          {h.change >= 0 ? "+" : ""}{h.change}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Local stats */}
          <div style={card}>
            <div style={label}>Local Stats</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 10 }}>
              <Stat v={wins}   s="Wins"   c={DS.success} />
              <Stat v={losses} s="Losses" c={DS.danger}  />
              <Stat v={draws}  s="Draws"  c={DS.warning} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
              <Stat v={`${winRate}%`}    s="Win Rate"   c={winRate >= 50 ? DS.success : DS.danger} />
              <Stat v={`${maxStreak} ✶`} s="Best Streak" />
              <Stat v={gamesPlayed}      s="Games" />
            </div>
          </div>

          {gamesPlayed > 0 && (
            <div style={card}>
              <div style={label}>Result Breakdown</div>
              <div style={{ height: 10, borderRadius: 5, overflow: "hidden", background: DS.elevated, display: "flex" }}>
                {wins   > 0 && <div style={{ flex: wins,   background: DS.success }} />}
                {draws  > 0 && <div style={{ flex: draws,  background: DS.warning }} />}
                {losses > 0 && <div style={{ flex: losses, background: DS.danger  }} />}
              </div>
              <div style={{ display: "flex", gap: 20, marginTop: 10, fontSize: 12, color: DS.textMuted }}>
                <span><span style={{ color: DS.success, marginRight: 4 }}>■</span>Wins</span>
                <span><span style={{ color: DS.warning, marginRight: 4 }}>■</span>Draws</span>
                <span><span style={{ color: DS.danger,  marginRight: 4 }}>■</span>Losses</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ v, s, c }) {
  return (
    <div style={{ background: DS.elevated, border: `1px solid ${DS.borderFaint}`, borderRadius: 10, padding: "14px 12px", textAlign: "center" }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: c || DS.text }}>{v}</div>
      <div style={{ fontSize: 10, color: DS.textFaint, marginTop: 5, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>{s}</div>
    </div>
  );
}

const card  = { background: DS.surface, border: `1px solid ${DS.borderFaint}`, borderRadius: DS.radius, padding: "18px 20px", marginBottom: 16 };
const label = { fontFamily: "'Cinzel', Georgia, serif", fontSize: 10, fontWeight: 700, color: DS.textFaint, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 };
